// =============================================================================
// VandieZ agent-web -- Application root
// =============================================================================
// Wires the auth provider + router. Routes:
//
//   /login                       -> LoginPage              (public)
//   /accept                      -> AcceptInvitationPage   (public)
//   /                            -> MyOwnersPage           (protected)
//   /vehicles                    -> VehiclesPage           (protected)
//   /vehicles/:vehicleId         -> VehicleDetailPage      (protected)
//   /marketplace                 -> MarketplacePage        (protected)
//   /marketplace/:loadId         -> LoadBidPage            (protected)
//   /bids                        -> BidsPage               (protected)
//   /trips                       -> TripsPage              (protected)
//   /trips/:tripId               -> TripDetailPage         (protected)
//   *                            -> redirect to /
// =============================================================================

import type React from "react";
import { BrowserRouter, Navigate, Route, Routes } from "react-router-dom";

import { ProtectedRoute } from "@/components/ProtectedRoute";
import { AuthProvider } from "@/lib/auth";
import { OwnerContextProvider } from "@/lib/owner-context";
import { AcceptInvitationPage } from "@/pages/AcceptInvitationPage";
import { BidsPage } from "@/pages/BidsPage";
import { BrokerPage } from "@/pages/BrokerPage";
import { LoadBidPage } from "@/pages/LoadBidPage";
import { LoginPage } from "@/pages/LoginPage";
import { MarketplacePage } from "@/pages/MarketplacePage";
import { MyLoadsPage } from "@/pages/MyLoadsPage";
import { PaymentsPage } from "@/pages/PaymentsPage";
import { ProfilePage } from "@/pages/ProfilePage";
import { ReportsPage } from "@/pages/ReportsPage";
import { PostLoadPage } from "@/pages/PostLoadPage";
import { DashboardPage } from "@/pages/DashboardPage";
import { MyOwnersPage } from "@/pages/MyOwnersPage";
import { TripDetailPage } from "@/pages/TripDetailPage";
import { TripsPage } from "@/pages/TripsPage";
import { VehicleDetailPage } from "@/pages/VehicleDetailPage";
import { VehiclesPage } from "@/pages/VehiclesPage";

export function App(): React.JSX.Element {
  return (
    <BrowserRouter>
      <AuthProvider>
        <OwnerContextProvider>
          <Routes>
            <Route path="/login" element={<LoginPage />} />
            <Route path="/accept" element={<AcceptInvitationPage />} />
            {/* AG-11. The nav has labelled this "Dashboard" since it was
                written and it rendered MyOwnersPage -- an owner roster.
                Useful, but an agent logging in landed on a list instead of
                on the state of their business. The roster moved to /owners
                and kept its own nav entry rather than being buried. */}
            <Route
              path="/"
              element={
                <ProtectedRoute>
                  <DashboardPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/owners"
              element={
                <ProtectedRoute>
                  <MyOwnersPage />
                </ProtectedRoute>
              }
            />
            <Route path="/vehicles" element={<ProtectedRoute><VehiclesPage /></ProtectedRoute>} />
            <Route path="/vehicles/:vehicleId" element={<ProtectedRoute><VehicleDetailPage /></ProtectedRoute>} />
            <Route path="/post-load" element={<ProtectedRoute><PostLoadPage /></ProtectedRoute>} />
            <Route path="/my-loads" element={<ProtectedRoute><MyLoadsPage /></ProtectedRoute>} />
            <Route
              path="/payments"
              element={
                <ProtectedRoute>
                  <PaymentsPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/profile"
              element={
                <ProtectedRoute>
                  <ProfilePage />
            <Route
              path="/reports"
              element={
                <ProtectedRoute>
                  <ReportsPage />
                </ProtectedRoute>
              }
            />
                </ProtectedRoute>
              }
            />
            <Route path="/marketplace" element={<ProtectedRoute><MarketplacePage /></ProtectedRoute>} />
            <Route path="/marketplace/:loadId" element={<ProtectedRoute><LoadBidPage /></ProtectedRoute>} />
            <Route path="/bids" element={<ProtectedRoute><BidsPage /></ProtectedRoute>} />
            <Route path="/broker" element={<ProtectedRoute><BrokerPage /></ProtectedRoute>} />
            <Route path="/trips" element={<ProtectedRoute><TripsPage /></ProtectedRoute>} />
            <Route path="/trips/:tripId" element={<ProtectedRoute><TripDetailPage /></ProtectedRoute>} />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </OwnerContextProvider>
      </AuthProvider>
    </BrowserRouter>
  );
}
